import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getContactInfoTranslations } from '@common/i18n/contactInfo';
import type { JSONCompatible } from '@common/types';
import { type ContactFormValues, ContactFormValuesSchema } from '@common/types/formSubmissions';
import type { QuestionSchema } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import {
    type ContactBlockEntry,
    canAddContactBlockEntry,
    createEmptyContactBlockEntry,
    ensureContactBlockEntries,
    getContactBlockConfig,
    getContactBlockEntryTitle,
    getFilledContactBlockEntries,
    isContactBlockEntryBlank,
    parseContactBlockValue,
    serializeContactBlockValue,
} from '@common/utils/contactBlock';
import { getVisibleQuestions } from '@common/utils/questionVisibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import ContactInfoSection from '@/pages/QuoteIntakeClientView/IntakeFormView/ContactInfoSection';
import {
    type ContactBlockEntryErrors,
    hasContactFormErrors,
} from '@/pages/QuoteIntakeClientView/IntakeFormView/contactValidation';
import { toFormValueString } from '@/pages/QuoteIntakeClientView/IntakeFormView/types';
import FormComponent from './FormComponent';

interface Props {
    schema: QuestionSchema;
    /** Required blocks need at least one filled-in entry; optional ones can be left blank. */
    isRequired: boolean;
    value?: string;
    entryErrors?: ContactBlockEntryErrors[];
    disabled?: boolean;
    readOnly?: boolean;
    lawFirmId?: string | number;
    language?: TemplateFormLanguage;
    onValueChange?: (name: string, value: string) => void;
}

export default function ContactBlockQuestion({
    schema,
    isRequired,
    value,
    entryErrors,
    disabled = false,
    readOnly = false,
    lawFirmId,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    onValueChange,
}: Props) {
    const t = getContactInfoTranslations(language);
    const config = getContactBlockConfig(schema);
    const questions = config.questions ?? [];
    const isEditable = !disabled && !readOnly && !!onValueChange;
    // The form builder renders questions disabled; read-only views show submitted answers instead
    const isPreview = disabled && !readOnly;
    const [collapsedEntryIds, setCollapsedEntryIds] = useState<Record<string, boolean>>({});

    // Memoized on the serialized value so a padded entry keeps a stable id until the first edit persists it
    const allEntries = useMemo(
        () => ensureContactBlockEntries(parseContactBlockValue(value).entries),
        [value],
    );
    // Read-only views show only what was filled in, like the intake summary
    const entries = readOnly ? getFilledContactBlockEntries(value, schema) : allEntries;

    // One event can commit several changes before the new value comes back as a prop (changing an
    // ID type clears its uploads first), so later commits build on earlier ones, not stale `entries`
    const pendingCommitRef = useRef<{ baseValue?: string; entries: ContactBlockEntry[] } | null>(
        null,
    );
    const getLatestEntries = () => {
        const pending = pendingCommitRef.current;
        return pending && pending.baseValue === value ? pending.entries : entries;
    };

    const commit = (nextEntries: ContactBlockEntry[]) => {
        if (!isEditable) return;
        pendingCommitRef.current = { baseValue: value, entries: nextEntries };
        onValueChange?.(schema.name, serializeContactBlockValue({ entries: nextEntries }));
    };

    const updateEntry = (
        index: number,
        update: (entry: ContactBlockEntry) => ContactBlockEntry,
    ) => {
        commit(getLatestEntries().map((entry, i) => (i === index ? update(entry) : entry)));
    };

    const handleContactChange = (
        index: number,
        field: keyof ContactFormValues,
        fieldValue: unknown,
    ) => {
        updateEntry(index, (entry) => ({
            ...entry,
            contact: { ...entry.contact, [field]: fieldValue },
        }));
    };

    const handleAnswerChange = (index: number, questionName: string, answer: JSONCompatible) => {
        updateEntry(index, (entry) => ({
            ...entry,
            answers: { ...entry.answers, [questionName]: toFormValueString(answer) },
        }));
    };

    // Removing the only entry of an optional block clears it, since a blank entry takes its place
    const canRemove =
        isEditable &&
        (entries.length > 1 || (!isRequired && !isContactBlockEntryBlank(entries[0], questions)));
    const canAdd = canAddContactBlockEntry(entries.length, config);
    const hasTooManyEntries = config.maxEntries !== undefined && entries.length > config.maxEntries;

    const handleAddEntry = () => {
        const latestEntries = getLatestEntries();
        if (!canAddContactBlockEntry(latestEntries.length, config)) return;
        commit([...latestEntries, createEmptyContactBlockEntry()]);
    };

    let addEntryButton: ReactNode = null;
    if (isEditable && !canAdd && config.maxEntries !== undefined) {
        addEntryButton = (
            <Typography
                variant="body2"
                sx={{ color: hasTooManyEntries ? 'error.main' : 'neutral.N_500' }}
            >
                {hasTooManyEntries
                    ? t.contactBlock.tooManyEntries(config.maxEntries)
                    : t.contactBlock.maxEntriesReached(config.maxEntries)}
            </Typography>
        );
    } else if (isEditable || isPreview) {
        addEntryButton = (
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!isEditable}
                onClick={handleAddEntry}
                sx={{ textTransform: 'none', width: 'fit-content' }}
            >
                {t.contactBlock.addEntry(config.entryLabel)}
            </Button>
        );
    }

    if (entries.length === 0) {
        return (
            <Typography variant="body2" sx={{ mt: 1, color: 'neutral.N_500' }}>
                {t.contactBlock.none}
            </Typography>
        );
    }

    return (
        <Stack spacing={2} sx={{ width: '100%', mt: 1 }}>
            {entries.map((entry, index) => {
                const errors = entryErrors?.[index];
                const hasErrors =
                    hasContactFormErrors(errors?.contact) ||
                    Object.values(errors?.answers ?? {}).some(Boolean);
                const entryTitle = getContactBlockEntryTitle(
                    config.entryLabel,
                    index,
                    entries.length,
                );
                const visibleQuestions = getVisibleQuestions(
                    questions,
                    (question) => entry.answers[question.questionSchema.name],
                );

                return (
                    <ContactInfoSection
                        key={entry.entryId}
                        index={index}
                        totalContacts={entries.length}
                        title={entryTitle}
                        values={entry.contact}
                        errors={errors?.contact}
                        onChange={(field, fieldValue) =>
                            handleContactChange(index, field, fieldValue)
                        }
                        lawFirmId={lawFirmId === undefined ? undefined : String(lawFirmId)}
                        disabledFields={disabled ? DISABLED_ALL : undefined}
                        hideContactFields={config.hideContactFields}
                        hiddenFields={config.hiddenFields}
                        requiredFields={config.requiredFields}
                        optionalCoreFields={config.optionalCoreFields}
                        description={config.description}
                        language={language}
                        expanded={hasErrors || !collapsedEntryIds[entry.entryId]}
                        onExpandedChange={(isExpanded) =>
                            setCollapsedEntryIds((prev) => ({
                                ...prev,
                                [entry.entryId]: !isExpanded,
                            }))
                        }
                        headerAction={
                            canRemove && (
                                <Tooltip title={t.contactBlock.removeEntry(entryTitle)}>
                                    <IconButton
                                        size="small"
                                        aria-label={t.contactBlock.removeEntry(entryTitle)}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            commit(
                                                getLatestEntries().filter((_, i) => i !== index),
                                            );
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    >
                        {visibleQuestions.map((question) => {
                            const questionName = question.questionSchema.name;
                            const answerError = errors?.answers[questionName] ?? null;
                            return (
                                <FormComponent
                                    key={questionName}
                                    fieldObj={question}
                                    lawFirmId={lawFirmId ?? ''}
                                    language={language}
                                    containerPadding="0"
                                    value={
                                        entry.answers[questionName] ?? question.questionSchema.value
                                    }
                                    isError={!!answerError}
                                    helper={answerError ?? undefined}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    onFieldValueChange={(_name, answer) =>
                                        handleAnswerChange(index, questionName, answer)
                                    }
                                />
                            );
                        })}
                    </ContactInfoSection>
                );
            })}
            {addEntryButton}
            {isPreview && (
                <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                    {isRequired
                        ? t.contactBlock.previewHintRequired(config.maxEntries)
                        : t.contactBlock.previewHintOptional(config.maxEntries)}
                </Typography>
            )}
        </Stack>
    );
}

const DISABLED_ALL: Partial<Record<keyof ContactFormValues, boolean>> = Object.fromEntries(
    ContactFormValuesSchema.keyof().options.map((field) => [field, true]),
);
